/**
 * .u-capitalize
 * CSS: `.u-capitalize` => lowercase
 * JS: Wrap the first letter in <span class="u-uppercase"> so that it appears in uppercase
 */
function wrapFirstLetterInUUppercase(el) {
  const element = el;
  if (!element || !(element instanceof Element)) return;

  // Idempotente: si ya está procesado, no lo toques.
  if (element.dataset.uCapitalizeProcessed === "true") return;
  if (
    element.firstElementChild &&
    element.firstElementChild.classList.contains("u-uppercase")
  ) {
    element.dataset.uCapitalizeProcessed = "true";
    return;
  }

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();

  while (node) {
    const text = node.nodeValue || "";
    const idx = text.search(/[A-Za-zÀ-ÿ]/); // primera “letra” (incluye acentos)

    if (idx !== -1) {
      const before = text.slice(0, idx);
      const letter = text.charAt(idx);
      const after = text.slice(idx + 1);

      const frag = document.createDocumentFragment();
      if (before) frag.appendChild(document.createTextNode(before));

      const span = document.createElement("span");
      span.className = "u-uppercase";
      span.textContent = letter;
      frag.appendChild(span);

      if (after) frag.appendChild(document.createTextNode(after));

      if (node.parentNode) {
        node.parentNode.insertBefore(frag, node);
        node.parentNode.removeChild(node);
      }

      element.dataset.uCapitalizeProcessed = "true";
      return;
    }

    node = walker.nextNode();
  }

  element.dataset.uCapitalizeProcessed = "true";
}

export function applyUCapitalize(root = document) {
  root.querySelectorAll(".u-capitalize").forEach(wrapFirstLetterInUUppercase);
}

function initUCapitalize() {
  applyUCapitalize(document);

  // Para contenido que aparece después (Barba/Ajax/accordion)
  if (!("MutationObserver" in window) || !document.body) return;

  let rafId = null;
  const schedule = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      applyUCapitalize(document);
    });
  };

  new MutationObserver((mutations) => {
    const hasAddedNodes = mutations.some(
      (m) => m.addedNodes && m.addedNodes.length > 0,
    );
    if (hasAddedNodes) schedule();
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener("barba:after", schedule);
  document.addEventListener("barba:afterEnter", schedule);
  document.addEventListener("barba:transitionEnd", schedule);
}

/**
 * Duplicate .grid-nav for mobile and move it as first child of <main>
 */
export function duplicateGridNavToMain() {
  const hasAside = document.querySelector(
    ".single-content aside, .grid__content aside",
  );

  // Only duplicate the nav when the page has the expected aside layout.
  if (!hasAside) return;

  const originalGridNav = document.querySelector(".grid-nav");
  const mainEl = document.querySelector("main");

  if (
    originalGridNav &&
    mainEl &&
    !document.querySelector(".u-grid-nav-mobile[data-grid-nav-duplicate]")
  ) {
    const gridNavClone = originalGridNav.cloneNode(true);
    gridNavClone.classList.add("u-grid-nav-mobile");
    gridNavClone.setAttribute("data-grid-nav-duplicate", "true");
    mainEl.insertBefore(gridNavClone, mainEl.firstChild);
  }
}
// Toggle 'open' class on section by data-target click
export function enableSectionToggle() {
  document.addEventListener("click", function handleSectionToggleClick(e) {
    const header = e.target.closest("[data-target^='section-']");

    if (header) {
      header.classList.toggle("u-section-open");
      const targetId = header.getAttribute("data-target");
      if (targetId) {
        const section = document.getElementById(targetId);
        if (section) {
          section.classList.toggle("u-content-open");
        }
      }
    }
  });
}

const OPA_MOBILE_VIDEO_BREAKPOINT = 768;

const OPA_GRID_CARD_STORAGE_KEY = "opaGridCardId";

function opaStoreVideoSources(video) {
  const currentVideo = video;

  if (currentVideo.dataset.opaVideoSrcStored === "true") {
    return;
  }

  const videoSrc = currentVideo.getAttribute("src");
  if (videoSrc) {
    currentVideo.dataset.opaVideoSrc = videoSrc;
  }

  const sources = currentVideo.querySelectorAll("source");
  sources.forEach((source) => {
    const sourceEl = source;
    const sourceSrc = sourceEl.getAttribute("src");
    if (sourceSrc) {
      sourceEl.dataset.opaSourceSrc = sourceSrc;
    }
  });

  currentVideo.dataset.opaVideoSrcStored = "true";
}

function opaDisableVideoOnMobile(video) {
  const currentVideo = video;
  opaStoreVideoSources(currentVideo);

  currentVideo.pause();
  currentVideo.removeAttribute("src");

  const sources = currentVideo.querySelectorAll("source");
  sources.forEach((source) => {
    const sourceEl = source;
    sourceEl.removeAttribute("src");
  });

  currentVideo.load();
  currentVideo.dataset.opaVideoDisabled = "true";
}

function opaRestoreVideoOnDesktop(video) {
  const currentVideo = video;
  if (currentVideo.dataset.opaVideoDisabled !== "true") {
    return;
  }

  if (currentVideo.dataset.opaVideoSrc) {
    currentVideo.setAttribute("src", currentVideo.dataset.opaVideoSrc);
  }

  const sources = currentVideo.querySelectorAll("source");
  sources.forEach((source) => {
    const sourceEl = source;
    if (sourceEl.dataset.opaSourceSrc && !sourceEl.getAttribute("src")) {
      sourceEl.setAttribute("src", sourceEl.dataset.opaSourceSrc);
    }
  });

  currentVideo.load();
  currentVideo.dataset.opaVideoDisabled = "false";
}

function opaApplyMobileVideoPolicy(root = document) {
  const videos = root.querySelectorAll("video");
  if (!videos.length) return;

  const isMobile = window.innerWidth < OPA_MOBILE_VIDEO_BREAKPOINT;

  videos.forEach((video) => {
    if (video.dataset.disableMobileVideo !== "true") {
      return;
    }

    if (isMobile) {
      opaDisableVideoOnMobile(video);
    } else {
      opaRestoreVideoOnDesktop(video);
    }
  });
}

function opaGetStoredGridCardPayload() {
  try {
    return JSON.parse(localStorage.getItem(OPA_GRID_CARD_STORAGE_KEY));
  } catch {
    return null;
  }
}

function opaRestoreGridCardFromStorage() {
  const payload = opaGetStoredGridCardPayload();
  if (!payload || !payload.cardId || !payload.path) {
    return false;
  }

  const currentPath = window.location.pathname + window.location.search;
  if (payload.path !== currentPath) {
    return false;
  }

  const target = document.getElementById(payload.cardId);
  if (!target) {
    return false;
  }

  const hash = `#${payload.cardId}`;
  if (window.location.hash !== hash) {
    window.history.replaceState(
      window.history.state || {},
      "",
      `${currentPath}${hash}`,
    );
  }

  const isFirefox = /firefox/i.test(navigator.userAgent);
  const headerOffset = isFirefox ? 170 : 60;
  const getScrollableParent = (el) => {
    let current = el.parentElement;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const canScrollY =
        (style.overflowY === "auto" || style.overflowY === "scroll") &&
        current.scrollHeight > current.clientHeight;
      if (canScrollY) {
        return current;
      }
      current = current.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const scrollContainer = getScrollableParent(target);
  const scrollWindow =
    scrollContainer === document.body ||
    scrollContainer === document.documentElement ||
    scrollContainer === document.scrollingElement;

  const scrollToPosition = (top) => {
    const nextTop = Math.max(0, top);
    if (scrollWindow) {
      window.scrollTo(0, nextTop);
      document.documentElement.scrollTop = nextTop;
      document.body.scrollTop = nextTop;
    } else {
      scrollContainer.scrollTop = nextTop;
    }
  };

  const performScroll = () => {
    const targetRect = target.getBoundingClientRect();
    if (scrollWindow) {
      const scrollBase = window.pageYOffset || window.scrollY || 0;
      scrollToPosition(targetRect.top + scrollBase - headerOffset);
    } else {
      const containerRect = scrollContainer.getBoundingClientRect();
      const offsetTop =
        targetRect.top - containerRect.top + scrollContainer.scrollTop;
      scrollToPosition(offsetTop - headerOffset);
    }

    setTimeout(() => {
      const rectTop = target.getBoundingClientRect().top;
      const delta = rectTop - headerOffset;
      if (Math.abs(delta) > 1) {
        if (scrollWindow) {
          scrollToPosition((window.pageYOffset || window.scrollY || 0) + delta);
        } else {
          scrollToPosition(scrollContainer.scrollTop + delta);
        }
      }
    }, 120);
  };

  const gridSection = target.closest(".opa-grid");
  const images = gridSection
    ? Array.from(gridSection.querySelectorAll("img"))
    : [];
  const pendingImages = images.filter((img) => !img.complete);

  if (pendingImages.length === 0) {
    performScroll();
  } else {
    let remaining = pendingImages.length;
    const onDone = () => {
      remaining -= 1;
      if (remaining <= 0) {
        performScroll();
      }
    };

    pendingImages.forEach((img) => {
      img.addEventListener("load", onDone, { once: true });
      img.addEventListener("error", onDone, { once: true });
    });

    setTimeout(() => {
      performScroll();
    }, 300);
  }

  localStorage.removeItem(OPA_GRID_CARD_STORAGE_KEY);
  return true;
}

function opaTryRestoreGridCard(maxTries = 30) {
  if (!localStorage.getItem(OPA_GRID_CARD_STORAGE_KEY)) {
    return;
  }

  let tries = 0;
  const attempt = () => {
    if (opaRestoreGridCardFromStorage()) {
      return;
    }

    tries += 1;
    if (tries < maxTries) {
      requestAnimationFrame(attempt);
    }
  };

  requestAnimationFrame(attempt);
}

function opaRemoveGridFadeOnAnchor() {
  if (!window.location.hash.startsWith("#grid-item-")) {
    return;
  }

  requestAnimationFrame(() => {
    document
      .querySelectorAll(".opa-grid .grid-list__item.u-animate-fade")
      .forEach((item) => {
        item.classList.remove("u-animate-fade");
      });
  });
}

function opaResetGridLoadMoreCount() {
  try {
    localStorage.removeItem("opaGridLoadMoreCount");
  } catch {
    // ignore storage errors
  }
}

function opaBindMenuLoadMoreReset() {
  document.addEventListener("click", (event) => {
    const menuLink = event.target.closest(".menu-item a");
    const btn = event.target.closest(".btn");
    const isLoadMore = btn && btn.id === "grid-loadmore";
    if (!menuLink && !(btn && !isLoadMore)) {
      return;
    }

    opaResetGridLoadMoreCount();
  });
}
// Check a CSS class to know if a module should be imported
window.checkIfModuleShouldBeLoaded = function checkIfModuleShouldBeLoaded(
  selectorToCheck,
) {
  if (selectorToCheck !== "") {
    const module = document.querySelectorAll(selectorToCheck);

    if (module[0]) {
      return true;
    }
  }

  return false;
};

// Load JS libraries
window.loadJsLibraries = function loadJsLibraries(libraries) {
  Object.entries(libraries).forEach(([key, value]) => {
    if (window.checkIfModuleShouldBeLoaded(value.cssClassName) === true) {
      import(/* webpackChunkName: "[request]" */ `./${value.fileName}`).then(
        ({ default: ObjectClass }) => {
          window.libraries[`"${key}"`] = new ObjectClass(value.cssClassName);
        },
      );
    }
  });
};

// Ajax requests
window.ajaxReq = function ajaxReq() {
  if (window.XMLHttpRequest) {
    return new XMLHttpRequest();
  }

  if (window.ActiveXObject) {
    return new ActiveXObject("Microsoft.XMLHTTP");
  }

  console.warn("Browser does not support XMLHTTP."); // eslint-disable-line no-console
  return false;
};

// Load JS scripts
window.loadJS = function loadJS(file, callback, defer = false, async = true) {
  if (document.body.querySelector(`script[src*="${file}"]`) != null) {
    callback();
    return;
  }

  // DOM: Create the script element
  const jsElm = document.createElement("script");
  // set the type attribute
  jsElm.type = "text/javascript";
  // make the script element load file
  jsElm.src = file;
  // add a callback
  jsElm.addEventListener("load", callback);
  // defer load
  if (defer === true) {
    jsElm.defer = true;
  }
  // async load
  if (async === true) {
    jsElm.async = true;
  }
  // finally insert the element to the body element in order to load the script
  document.body.appendChild(jsElm);
};

/**
 * Adds or removes transition styles to slide elements during Swiper slide change transitions.
 * @param {Object} swiperInstance - The Swiper instance.
 * @param {string} action - Either 'start' or 'end' to indicate the transition phase.
 */
export function handleSlideTransition(
  swiperInstance,
  action,
  isNextTransition = true,
) {
  const swiperWrapper = swiperInstance.el.querySelector(".swiper-wrapper");
  if (swiperWrapper) {
    swiperWrapper.classList.toggle("is-slowing", action === "start");
  }

  swiperInstance.slides.forEach((slide, index) => {
    const contents = slide.querySelectorAll(".slide_media");
    const contentsText = slide.querySelectorAll(".slide_text");

    if (
      action === "start" &&
      isNextTransition &&
      index === swiperInstance.previousIndex
    ) {
      if (contents.length) {
        contents.forEach((contentEl) => {
          contentEl.classList.add("slide_media_effect");
        });
        // console.log("start");
      }
      if (contentsText.length) {
        contentsText.forEach((textEl) => {
          textEl.classList.add("slide_text_effect");
        });
        // console.log("end");
      }
    }

    // Inverse animation for prev direction
    if (
      action === "start" &&
      !isNextTransition &&
      index === swiperInstance.previousIndex
    ) {
      if (contents.length) {
        contents.forEach((contentEl) => {
          contentEl.classList.add("slide_media_effect_inverse");
        });
      }
      if (contentsText.length) {
        contentsText.forEach((textEl) => {
          textEl.classList.add("slide_text_effect_inverse");
        });
      }
    }

    if (action === "end") {
      if (contents.length) {
        contents.forEach((contentEl) => {
          contentEl.classList.remove(
            "slide_media_effect",
            "slide_media_effect_inverse",
          );
        });
      }
      if (contentsText.length) {
        contentsText.forEach((textEl) => {
          textEl.classList.remove(
            "slide_text_effect",
            "slide_text_effect_inverse",
          );
        });
      }
    }
  });
}

/**
 * Returns a standardized set of Swiper event handlers for slide transitions.
 * @returns {Object} Swiper event callbacks.
 */
export function getStandardSlideEvents() {
  return {
    slideChangeTransitionStart(swiper) {
      const isNext =
        swiper.swipeDirection === "next" ||
        swiper.activeIndex > swiper.previousIndex;

      handleSlideTransition(this, "start", isNext);

      if (isNext) {
        swiper.el.classList.add("swiper-direction-next");
        swiper.el.classList.remove("swiper-direction-prev");
      } else if (
        swiper.swipeDirection === "prev" ||
        swiper.activeIndex < swiper.previousIndex
      ) {
        swiper.el.classList.add("swiper-direction-prev");
        swiper.el.classList.remove("swiper-direction-next");
      }
    },
    slideChangeTransitionEnd() {
      handleSlideTransition(this, "end");
    },
  };
}

/**
 * Simple Modal System
 * Handles modal-open and modal-close attributes globally across the theme
 */

function initModalSystem() {
  // Handle nav open buttons
  document.addEventListener("click", function handleNavOpenClick(e) {
    const openButton = e.target.closest("[nav-open]");
    if (openButton) {
      e.preventDefault();
      const modalId = openButton.getAttribute("nav-open");
      // remove class to modalId if it exists
      const existingModal = document.getElementById(modalId);
      if (existingModal) {
        existingModal.classList.add("active");
      }
      if (modalId === "navAside" || modalId === "navFilters") {
        document.body.classList.add("u-block-scroll-mobile");
      }
      document.body.classList.add("u-has-nav-open");
    }
  });

  // Handle modal open buttons
  document.addEventListener("click", function handleModalOpenClick(e) {
    const openButton = e.target.closest("[modal-open]");
    if (openButton) {
      e.preventDefault();
      const modalId = openButton.getAttribute("modal-open");
      window.openModal(modalId);
    }
  });

  // Handle modal close buttons
  document.addEventListener("click", function handleModalCloseClick(e) {
    const closeButton = e.target.closest("[modal-close]");
    if (closeButton) {
      e.preventDefault();
      const modalId = closeButton.getAttribute("modal-close");
      window.closeModal(modalId);
      if (modalId === "navAside" || modalId === "navFilters") {
        document.body.classList.remove("u-block-scroll-mobile");
        document.body.classList.remove("u-has-nav-open");
      }
    }
  });

  // Close modal on escape key
  document.addEventListener("keydown", function handleModalKeydown(e) {
    if (e.key === "Escape") {
      window.closeAllModals();
    }
  });

  // Close modal on backdrop click
  document.addEventListener("click", function handleModalBackdropClick(e) {
    if (e.target.classList.contains("modal-backdrop")) {
      window.closeAllModals();
    }
  });
}

/**
 * Open modal by ID
 * @param {string} modalId - The modal ID to open
 */
window.openModal = function openModal(modalId) {
  // Close any existing modals first
  window.closeAllModals();

  // Find the modal element
  const modalElement = document.getElementById(modalId);
  if (modalElement) {
    // Add active class to show modal
    modalElement.classList.add("active");

    // Add modal-open class to body for CSS targeting and overflow control
    document.body.classList.add("modal-open");
    document.body.classList.add(`modal-open-${modalId}`);
    document.body.classList.remove("u-block-scroll-mobile"); // Add this line

    // Trigger custom event
    document.dispatchEvent(
      new CustomEvent("modalOpened", {
        detail: { modalId, modalElement },
      }),
    );
  }
};

/**
 * Close specific modal by ID
 * @param {string} modalId - The modal ID to close
 */
window.closeModal = function closeModal(modalId) {
  const modalElement = document.getElementById(modalId);
  if (modalElement) {
    // Remove active class to hide modal
    modalElement.classList.remove("active");

    // Remove modal-open classes from body
    document.body.classList.remove("modal-open");
    document.body.classList.remove(`modal-open-${modalId}`);

    // Trigger custom event
    document.dispatchEvent(
      new CustomEvent("modalClosed", {
        detail: { modalId },
      }),
    );
  }
};

/**
 * Close all active modals
 */
window.closeAllModals = function closeAllModals() {
  // Find all elements with active class that could be modals
  const activeModals = document.querySelectorAll(".active[id]");

  activeModals.forEach((modal) => {
    modal.classList.remove("active");
  });

  // Remove all modal-open classes from body
  document.body.classList.remove("modal-open");
  document.body.className = document.body.className.replace(
    /modal-open-\w+/g,
    "",
  );

  // Trigger custom event
  document.dispatchEvent(
    new CustomEvent("modalClosed", {
      detail: { modalId: "all" },
    }),
  );
};

/**
 * Check if any modal is currently open
 * @returns {boolean}
 */
window.isModalOpen = function isModalOpen() {
  return document.querySelector(".active[id]") !== null;
};

/**
 * Line clamp utility
 * add classes to .twig:
	•	.u-text-height → Main paragraphs with dynamic clamping based on available space.
	•	.u-text-substr-height → “Subtracted” elements (titles, badges, CTAs, etc.) whose height is deducted from the container.
	•	.u-content-container → Container that defines the total height available for text and sub-blocks.
	•	.u-text-height-caption → Captions with their own calculated line-height.
	•	.u-text-substr-height-caption → Elements that reduce the available height for the caption.
	•	.u-text-height-boxed → Special variant whose clamp is calculated based on its own rendered height.
 */

export function getFirstLineHeight(el) {
  if (!el) return 0;

  const cs = window.getComputedStyle(el);
  const { fontSize: fontSizeRaw, lineHeight } = cs;
  const fontSize = parseFloat(fontSizeRaw) || 0;

  if (lineHeight && lineHeight !== "normal") {
    if (lineHeight.endsWith("px")) {
      return parseFloat(lineHeight) || 0;
    }

    const numeric = parseFloat(lineHeight);
    if (!Number.isNaN(numeric) && fontSize) {
      return numeric * fontSize;
    }
  }

  if (
    el.firstChild &&
    typeof el.firstChild.textContent === "string" &&
    el.firstChild.textContent.length > 0
  ) {
    const range = document.createRange();
    try {
      range.setStart(el.firstChild, 0);
      range.setEnd(el.firstChild, 1);
      const rect = range.getBoundingClientRect();
      if (rect.height > 0) {
        return rect.height;
      }
    } catch {
      /* silent fallback */
    }
  }

  if (fontSize) {
    return fontSize * 1.2;
  }

  return 0;
}

export function applyBoxedLineClamp() {
  const elements = Array.from(
    document.querySelectorAll(".u-text-height-boxed"),
  );
  if (!elements.length) return;

  const getVerticalMargins = (node) => {
    const cs = window.getComputedStyle(node);
    return (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
  };

  const viewportWidth =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth ||
    0;

  elements.forEach((el) => {
    // <= 768px: sin clamp, dejamos el texto libre
    if (viewportWidth <= 768) {
      el.style.removeProperty("display");
      el.style.removeProperty("-webkit-box-orient");
      el.style.removeProperty("-webkit-line-clamp");
      el.style.removeProperty("overflow");
      el.style.removeProperty("max-height");
      return;
    }

    // Reset estilos antes de medir
    el.style.removeProperty("display");
    el.style.removeProperty("-webkit-box-orient");
    el.style.removeProperty("-webkit-line-clamp");
    el.style.removeProperty("overflow");
    el.style.removeProperty("max-height");

    el.style.setProperty("display", "-webkit-box");
    el.style.setProperty("-webkit-box-orient", "vertical");
    // el.style.setProperty("-webkit-line-clamp", String(approxLines));
    el.style.setProperty("overflow", "hidden");

    const firstLineHeight = getFirstLineHeight(el);
    const { height: totalHeight } = el.getBoundingClientRect();
    const { scrollHeight } = el;
    let availableHeight = totalHeight;

    if (scrollHeight <= totalHeight + 1) {
      const { parentElement: parent } = el;
      if (parent) {
        const parentHeight = parent.getBoundingClientRect().height;
        if (parentHeight > 0 && parentHeight < scrollHeight) {
          let siblingsHeight = 0;
          Array.from(parent.children).forEach((child) => {
            if (child === el) return;
            const childRect = child.getBoundingClientRect();
            if (childRect.height <= 0 && child.offsetHeight <= 0) return;
            siblingsHeight += childRect.height + getVerticalMargins(child);
          });
          const candidateHeight = parentHeight - siblingsHeight;
          if (candidateHeight > 0) {
            availableHeight = Math.min(candidateHeight, scrollHeight);
          }
        }
      }
    }

    if (
      Number.isFinite(firstLineHeight) &&
      firstLineHeight > 0 &&
      Number.isFinite(availableHeight) &&
      availableHeight > 0
    ) {
      const approxLines = Math.floor(availableHeight / firstLineHeight);
      const adjustedLines = approxLines;
      if (adjustedLines > 0) {
        const clampHeight = adjustedLines * firstLineHeight + 6;

        el.style.setProperty("display", "-webkit-box");
        el.style.setProperty("-webkit-box-orient", "vertical");
        el.style.setProperty("-webkit-line-clamp", String(adjustedLines));
        el.style.setProperty("overflow", "hidden");
        el.style.setProperty("max-height", `${clampHeight}px`);
      }
    }
  });
}

export function watchTextLineHeightsCaption(selector, onChange) {
  const finalSelector = selector || ".u-text-height-caption";
  const elements = Array.from(document.querySelectorAll(finalSelector));
  if (!elements.length) return () => {};

  const prevLineHeights = new WeakMap();

  const setHeight = (el) => {
    const h = getFirstLineHeight(el);
    if (!Number.isFinite(h) || h <= 0) return;

    const prev = prevLineHeights.get(el) || 0;
    if (Math.abs(h - prev) < 0.5) {
      return;
    }

    prevLineHeights.set(el, h);
    // Guardamos en una variable específica para captions
    el.style.setProperty("--u-text-caption-line-h", `${h}px`);

    document.dispatchEvent(
      new CustomEvent("uTextLineHeightCaption", {
        detail: { el, height: h },
      }),
    );

    if (typeof onChange === "function") onChange(el, h);
  };

  const setAll = () => elements.forEach(setHeight);

  let ro = null;
  if ("ResizeObserver" in window) {
    ro = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setHeight(entry.target);
      });
    });
    elements.forEach((el) => ro.observe(el));
  } else {
    window.addEventListener("resize", setAll, { passive: true });
  }

  setAll();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(setAll).catch(() => {});
  }

  return () => {
    if (ro) {
      elements.forEach((el) => ro.unobserve(el));
      ro.disconnect();
    } else {
      window.removeEventListener("resize", setAll);
    }
  };
}

export function watchTextLineHeights(selector, onChange) {
  const finalSelector = selector || ".u-text-height";
  const elements = Array.from(document.querySelectorAll(finalSelector));
  if (!elements.length) return () => {};

  const prevLineHeights = new WeakMap();

  const setHeight = (el) => {
    const h = getFirstLineHeight(el);
    if (!Number.isFinite(h) || h <= 0) return;

    const prev = prevLineHeights.get(el) || 0;
    if (Math.abs(h - prev) < 0.5) {
      return;
    }

    prevLineHeights.set(el, h);
    el.style.setProperty("--u-text-line-h", `${h}px`);
    document.dispatchEvent(
      new CustomEvent("uTextLineHeight", { detail: { el, height: h } }),
    );
    if (typeof onChange === "function") onChange(el, h);
  };

  const setAll = () => elements.forEach(setHeight);

  let ro = null;
  if ("ResizeObserver" in window) {
    ro = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setHeight(entry.target);
      });
    });
    elements.forEach((el) => ro.observe(el));
  } else {
    window.addEventListener("resize", setAll, { passive: true });
  }

  setAll();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(setAll).catch(() => {});
  }

  return () => {
    if (ro) {
      elements.forEach((el) => ro.unobserve(el));
      ro.disconnect();
    } else {
      window.removeEventListener("resize", setAll);
    }
  };
}

export function watchTextContentHeights(selector, onChange) {
  const finalSelector = selector || ".u-content-container";
  const elements = Array.from(document.querySelectorAll(finalSelector));
  if (!elements.length) return () => {};

  const prevContentHeights = new WeakMap();
  const prevDiffHeights = new WeakMap();
  const lineClampAdjustment = new WeakMap();

  const setHeight = (el) => {
    const viewportWidth =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth ||
      0;

    const scope =
      el.closest("[data-lineclamp-scope]") ||
      el.closest(".featured-system_item") ||
      el.closest("section");

    let adjustment = lineClampAdjustment.get(el);
    if (adjustment == null) {
      adjustment = 1;
      lineClampAdjustment.set(el, adjustment);
    }

    if (viewportWidth <= 768) {
      const textHeightEl = scope
        ? scope.querySelector(".u-text-height:not(.u-text-height-boxed)")
        : null;
      if (textHeightEl) {
        textHeightEl.style.removeProperty("display");
        textHeightEl.style.removeProperty("-webkit-box-orient");
        textHeightEl.style.removeProperty("-webkit-line-clamp");
        textHeightEl.style.removeProperty("overflow");
        textHeightEl.style.removeProperty("max-height");
      }

      const captionEl = scope
        ? scope.querySelector(".u-text-height-caption")
        : null;
      if (captionEl) {
        captionEl.style.removeProperty("display");
        captionEl.style.removeProperty("-webkit-box-orient");
        captionEl.style.removeProperty("-webkit-line-clamp");
        captionEl.style.removeProperty("overflow");
        captionEl.style.removeProperty("max-height");
      }

      prevContentHeights.delete(el);
      prevDiffHeights.delete(el);

      return;
    }
    const h = el.offsetHeight;
    el.style.setProperty("--u-text-content-h", `${h}px`);
    document.dispatchEvent(
      new CustomEvent("uTextHeight", { detail: { el, height: h } }),
    );

    const parts = scope
      ? Array.from(scope.querySelectorAll(".u-text-substr-height"))
          .filter((n) => n.offsetParent !== null || n.offsetHeight > 0)
          .sort(
            (a, b) =>
              a.getBoundingClientRect().top - b.getBoundingClientRect().top,
          )
      : [];

    let sumH = 0;
    parts.forEach((node) => {
      const cs = window.getComputedStyle(node);
      const mt = parseFloat(cs.marginTop) || 0;
      const mb = parseFloat(cs.marginBottom) || 0;

      const contentHeight = node.getBoundingClientRect().height;
      const total = contentHeight + mt + mb;
      sumH += total;
    });
    el.style.setProperty("--u-text-substr-sum-h", `${sumH}px`);

    const diffH = h - sumH;

    prevContentHeights.set(el, h);
    prevDiffHeights.set(el, diffH);

    const textHeightEl = scope
      ? scope.querySelector(".u-text-height:not(.u-text-height-boxed)")
      : null;
    let lineHeight = 0;
    if (textHeightEl) {
      const varVal =
        textHeightEl.style.getPropertyValue("--u-text-line-h") ||
        window
          .getComputedStyle(textHeightEl)
          .getPropertyValue("--u-text-line-h");
      if (varVal && varVal.endsWith("px")) {
        lineHeight = parseFloat(varVal);
      } else {
        const cs = window.getComputedStyle(textHeightEl);
        const lh = cs.lineHeight;
        if (lh && lh !== "normal") {
          lineHeight = lh.endsWith("px")
            ? parseFloat(lh)
            : parseFloat(lh) * (parseFloat(cs.fontSize) || 0);
        } else {
          lineHeight = (parseFloat(cs.fontSize) || 0) * 1.2;
        }
      }
    }
    if (lineHeight > 0) {
      const numLinesTextHeight = diffH / lineHeight;

      const numLinesTextHeightCeil = Math.max(
        0,
        Math.ceil(numLinesTextHeight) - (lineClampAdjustment.get(el) || 1),
      );

      if (textHeightEl && numLinesTextHeightCeil > 0) {
        const lines = numLinesTextHeightCeil;

        textHeightEl.style.setProperty("display", "-webkit-box");
        textHeightEl.style.setProperty("-webkit-box-orient", "vertical");
        textHeightEl.style.setProperty("-webkit-line-clamp", String(lines));
        textHeightEl.style.setProperty("overflow", "hidden");
      }
    }

    const captionEl = scope
      ? scope.querySelector(".u-text-height-caption")
      : null;
    if (captionEl) {
      const varCaption =
        captionEl.style.getPropertyValue("--u-text-caption-line-h") ||
        window
          .getComputedStyle(captionEl)
          .getPropertyValue("--u-text-caption-line-h");
      let captionLineHeight = 0;
      if (varCaption && varCaption.endsWith("px")) {
        captionLineHeight = parseFloat(varCaption);
      } else {
        const csCaption = window.getComputedStyle(captionEl);
        const lhCaption = csCaption.lineHeight;
        const fsCaption = parseFloat(csCaption.fontSize) || 0;
        if (lhCaption && lhCaption !== "normal") {
          captionLineHeight = lhCaption.endsWith("px")
            ? parseFloat(lhCaption)
            : parseFloat(lhCaption) * fsCaption;
        } else if (fsCaption) {
          captionLineHeight = fsCaption * 1.2;
        }
      }
      if (captionLineHeight > 0) {
        const captionSubstrEl = scope
          ? scope.querySelector(".u-text-substr-height-caption")
          : null;

        let captionSubstrHeight = 0;
        if (captionSubstrEl) {
          const cs = window.getComputedStyle(captionSubstrEl);
          const mt = parseFloat(cs.marginTop) || 0;
          const mb = parseFloat(cs.marginBottom) || 0;
          captionSubstrHeight = captionSubstrEl.offsetHeight + mt + mb;
        }

        const availableHeightForCaption = h - captionSubstrHeight;
        const numLinesCaption = availableHeightForCaption / captionLineHeight;

        captionEl.style.setProperty(
          "--u-text-caption-max-lines",
          String(numLinesCaption),
        );

        const baseClamp = Math.ceil(numLinesCaption) - 1;
        const extraAdjust = captionSubstrEl ? 1 : 0;
        const numLinesCaptionCeil = Math.max(0, baseClamp - extraAdjust);
        if (numLinesCaptionCeil > 0) {
          const lines = numLinesCaptionCeil;

          captionEl.style.setProperty("display", "-webkit-box");
          captionEl.style.setProperty("-webkit-box-orient", "vertical");
          captionEl.style.setProperty("-webkit-line-clamp", String(lines));
          captionEl.style.setProperty("overflow", "hidden");
        }
      }
    }

    document.dispatchEvent(
      new CustomEvent("uTextSubstrSumHeight", {
        detail: { el, sum: sumH },
      }),
    );

    if (typeof onChange === "function") onChange(el, h);
  };

  const setAll = () => {
    elements.forEach((el) => setHeight(el));

    if (document.querySelector(".u-text-height-boxed")) {
      applyBoxedLineClamp();
    }
  };

  let rafId = null;
  const schedule = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(setAll);
  };

  let ro = null;
  if ("ResizeObserver" in window) {
    ro = new ResizeObserver((entries) => {
      entries.forEach(({ target }) => {
        setHeight(target);
      });
    });
    elements.forEach((el) => ro.observe(el));
  }

  window.addEventListener("resize", schedule, { passive: true });

  setAll();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(setAll).catch(() => {});
  }

  return () => {
    if (ro) {
      elements.forEach((el) => ro.unobserve(el));
      ro.disconnect();
      ro = null;
    }
    window.removeEventListener("resize", schedule);
  };
}

function initHeightWatchers() {
  try {
    if (document.querySelector(".u-content-container")) {
      watchTextContentHeights();
    }
    if (document.querySelector(".u-text-height-caption")) {
      watchTextLineHeightsCaption();
    }
    if (document.querySelector(".u-text-height-boxed")) {
      applyBoxedLineClamp();
      window.addEventListener("resize", applyBoxedLineClamp, {
        passive: true,
      });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("watchTextContentHeights init error", e);
  }
}

/**
 * Grid aside collapsible items
 */

function initGridAsideRelatedToggle() {
  const allContainers = document.querySelectorAll(".grid-aside-item");

  allContainers.forEach((container) => {
    const toggleTarget = container.querySelector(".related_ul");
    const header = container.querySelector(".grid-aside-item__text"); // Trigger exacto

    if (!toggleTarget || !header) return;

    container.addEventListener("click", (e) => {
      // Solo toggle si el click proviene del header
      if (!header.contains(e.target)) return;

      const isActive = toggleTarget.classList.contains("active");

      document.querySelectorAll(".related_ul.active").forEach((ul) => {
        ul.classList.remove("active");
        ul.closest(".grid-aside-item")?.classList.remove("active-parent");
      });

      if (!isActive) {
        toggleTarget.classList.add("active");
        container.classList.add("active-parent");
      }
    });
  });
}

document.addEventListener("DOMContentLoaded", () => {
  duplicateGridNavToMain();
  initModalSystem();
  initUCapitalize();
  opaApplyMobileVideoPolicy();
  opaTryRestoreGridCard();
  opaRemoveGridFadeOnAnchor();
  opaBindMenuLoadMoreReset();

  try {
    if (document.querySelector(".u-text-height")) {
      watchTextLineHeights();
    }
  } catch (e) {
    console.warn("watchTextLineHeights init error", e); // eslint-disable-line no-console
  }

  initHeightWatchers();
  initGridAsideRelatedToggle();
});

let opaVideoPolicyRaf = null;
const opaScheduleMobileVideoPolicy = () => {
  if (opaVideoPolicyRaf) {
    cancelAnimationFrame(opaVideoPolicyRaf);
  }

  opaVideoPolicyRaf = requestAnimationFrame(() => {
    opaVideoPolicyRaf = null;
    opaApplyMobileVideoPolicy();
  });
};

window.addEventListener("resize", opaScheduleMobileVideoPolicy, {
  passive: true,
});
document.addEventListener("barba:after", opaScheduleMobileVideoPolicy);
document.addEventListener("barba:afterEnter", opaScheduleMobileVideoPolicy);

document.addEventListener("barba:after", () => {
  opaTryRestoreGridCard();
  opaRemoveGridFadeOnAnchor();
});
document.addEventListener("barba:afterEnter", () => {
  opaTryRestoreGridCard();
  opaRemoveGridFadeOnAnchor();
});
window.addEventListener("pageshow", () => {
  opaTryRestoreGridCard();
  opaRemoveGridFadeOnAnchor();
});
